import React, { useState, useEffect } from 'react';
import { Save, X, Info } from 'lucide-react';

const DeviceFormModal = ({ show, onClose, onSave, device, deviceTypes }) => {
  const [formData, setFormData] = useState({
    deviceName: '',
    mqttIdentifier: '', // This is the string identifier for MQTT topics
    typeID: '',
    locationID: '',
    serialNumber: '',
    notes: '',
    // MQTT-specific configuration
    mqttTopics: {
      command: '', // Topic for sending commands (if applicable)
      telemetry: '', // Topic for receiving sensor data
      status: ''  // Topic for receiving status updates
    }
  });

  useEffect(() => {
    if (device) {
      // For existing devices, use the existing values
      setFormData({
        // Keep the numeric deviceID for database reference
        deviceID: device.deviceID, 
        // Use mqttIdentifier if available, otherwise use a safe default
        mqttIdentifier: device.mqttIdentifier || '',
        deviceName: device.deviceName || '',
        typeID: device.typeID || '',
        locationID: device.locationID || '',
        serialNumber: device.serialNumber || '',
        notes: device.notes || '',
        mqttTopics: {
          command: device.mqttTopics?.command || '',
          telemetry: device.mqttTopics?.telemetry || '',
          status: device.mqttTopics?.status || ''
        }
      });
    } else {
      // Reset to default values for new device
      setFormData({
        deviceName: '',
        mqttIdentifier: '',
        typeID: '',
        locationID: '',
        serialNumber: '',
        notes: '',
        mqttTopics: {
          command: '',
          telemetry: '',
          status: ''
        }
      });
    }
  }, [device, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clean up the MQTT identifier by removing any prefix
    const cleanMqttIdentifier = formData.mqttIdentifier ? 
      formData.mqttIdentifier.replace(/^winery\//, '') : '';
    
    // Prepare data for saving
    const saveData = {
      ...formData,
      mqttIdentifier: cleanMqttIdentifier, // Pass the cleaned MQTT identifier
      // Make sure to include the numeric deviceID if editing (for proper PUT URL)
      // deviceID will be included from formData if present from device
      // Ensure typeID is a number
      typeID: parseInt(formData.typeID),
    };
  
    // Validate required fields
    if (!saveData.mqttIdentifier || !saveData.deviceName || !saveData.typeID) {
      alert('MQTT Identifier, device name, and type are required fields');
      return;
    }
  
    onSave(saveData);
  };

  const handleTopicChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      mqttTopics: {
        ...prev.mqttTopics,
        [type]: value
      }
    }));
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{device ? 'Edit Device' : 'Add New Device'}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Device Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.deviceName}
                      onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                      required
                      placeholder="e.g., Barrel Room Sensor"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Device Type</label>
                    <select
                      className="form-select"
                      value={formData.typeID}
                      onChange={(e) => setFormData({ ...formData, typeID: e.target.value })}
                      required
                    >
                      <option value="">Select Device Type</option>
                      {deviceTypes.map((type) => (
                        <option key={type.typeID} value={type.typeID}>
                          {type.typeName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">MQTT Identifier</label>
                    <div className="input-group">
                      <span className="input-group-text">winery/</span>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.mqttIdentifier.replace(/^winery\//, '')}
                        onChange={(e) => setFormData({ ...formData, mqttIdentifier: e.target.value })}
                        required
                        placeholder="device1, sensor1, etc."
                      />
                    </div>
                    <div className="form-text">Unique identifier for MQTT topics</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Serial Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.serialNumber}
                      onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                      placeholder="Optional serial number"
                    />
                  </div>
                </div>

                {/* MQTT Topics Configuration */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0 d-flex align-items-center">
                      <Info size={16} className="me-2" />
                      MQTT Topic Configuration
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Command Topic</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.mqttTopics.command}
                          onChange={(e) => handleTopicChange('command', e.target.value)}
                          placeholder="Leave empty for sensor-only devices"
                        />
                        <div className="form-text">For devices that can receive commands</div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Telemetry Topic</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.mqttTopics.telemetry}
                          onChange={(e) => handleTopicChange('telemetry', e.target.value)}
                          placeholder="winery/device1/tele/SENSOR"
                        />
                        <div className="form-text">For receiving sensor readings</div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Status Topic</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.mqttTopics.status}
                          onChange={(e) => handleTopicChange('status', e.target.value)}
                          placeholder="winery/device1/stat/+"
                        />
                        <div className="form-text">For receiving device status updates</div>
                      </div>
                    </div>
                    <div className="alert alert-info py-2">
                      <small>
                        <Info size={14} className="me-1" />
                        Wildcards (+) can be used to listen to multiple subtopics. The MQTT identifier will be dynamically replaced.
                      </small>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    placeholder="Optional notes about this device"
                  ></textarea>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    <X size={16} className="me-1" />
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <Save size={16} className="me-1" />
                    {device ? 'Update Device' : 'Add Device'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeviceFormModal;